import {Component, OnDestroy, OnInit} from '@angular/core';
import {PermissionService} from "../../../service/permission.service";
import {LoginIdService} from "../../../service/login-id.service";
import {UserCompanyGroup} from "../../../model/userCompanyGroup";
import {NavigationEnd, Router} from "@angular/router";
import {startWith} from "rxjs/operators";
import {Subscription} from "rxjs";
import {ProductService} from "../../../service/product.service";
import {SnackbarService} from "../../../service/snackbar.service";
import * as Sentry from "@sentry/angular";

@Component({
  selector: 'app-company-login-selection',
  templateUrl: './company-login-selection.component.html',
  styleUrls: ['./company-login-selection.component.css']
})
export class CompanyLoginSelectionComponent implements OnInit, OnDestroy {
  userCompanyGroups: UserCompanyGroup[] = [];
  selected: string;
  redirectPaths = ['/order_details', '/order_object'];
  disabledPaths = ['/order_summary'];
  disabled: boolean = false;
  display: boolean = false;
  loginStatusSubscription: Subscription;

  constructor(private permissionService: PermissionService,
              private loginIdService: LoginIdService, private productService: ProductService,
              private router: Router,private readonly snackBarService: SnackbarService) {
  }

  ngOnInit(): void {
    this.loginStatusSubscription = this.loginIdService.loginStatusChanged$.pipe(
        startWith(this.loginIdService.getLoginStatus())).subscribe(status => {
      if (status) {
        this.getUserCompanyGroupsAndPlatformProfile();
      } else {
        this.display = false;
      }
    });

    const currentUrl = this.router.url;
    this.disabled = currentUrl.includes('cart') || currentUrl.includes('authority');

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentEventUrl = event.url;
        this.disabled = currentEventUrl.includes('cart') || currentEventUrl.includes('authority');
      }
    })
  }

  onSelected(value: string) {
    const currentUrl = this.router.url;
    this.loginIdService.updateLoginId(this.userCompanyGroups[value]);
    sessionStorage.setItem('loginId', this.userCompanyGroups[value].companyLoginId);

    // if the User changes the CompanyLogin while checking orderDetails or orderObjects redirect him to /orders
    if (this.redirectPaths.some(path => currentUrl.startsWith(path))) {
      this.router.navigate(['/orders']);
    }
  }

  getUserCompanyGroupsAndPlatformProfile(): void {
    if (this.loginIdService.getUserCompanyGroups().length === 0) {
      //save list of products in sessionStorage to prevent an additional spinner on new order page
      this.productService.getProducts().subscribe();
      this.permissionService.getUserCompanyGroupsFromPrincipal(true).subscribe(async res => {
        if(res.length === 0){
          await this.loginIdService.loggedOut(true);
        }
        this.userCompanyGroups = [];
        res.forEach(dto => {
          this.userCompanyGroups.push(new UserCompanyGroup(dto));
        });

        // find the users own companyLogin if present and place it as first element
        this.sortUserCompanyGroups();
        this.loginIdService.setUserCompanyGroups(this.userCompanyGroups);
        // if no loginId is set => use the first one available and set it
        if (sessionStorage.getItem('loginId') === null) {
          sessionStorage.setItem('loginId', this.userCompanyGroups[0].companyLoginId);
        }
        if (sessionStorage.getItem('loginId') !== null) {
          const index = this.userCompanyGroups.findIndex(login => login.companyLoginId === sessionStorage.getItem('loginId'));
          this.selected = index.toString();

          // after getting the userCompanyGroups fetch the platformProfile for the selected loginId
          await this.loginIdService.getPlatformProfile(this.loginIdService.createHeaders(this.userCompanyGroups[index].companyLoginId));
          // when the platformProfile is fetched for the currently active multi-user update the loginId
          this.loginIdService.updateLoginId(this.userCompanyGroups[index]);
          this.displayCompanyLoginSelection();
        } else {
          console.error('Invalid State of the CompanyLoginSelection!');
        }
      }, error => {
        this.handleUnexpectedError(error);
        console.log(error);
      });
    } else {
      this.userCompanyGroups = this.loginIdService.getUserCompanyGroups();
      this.sortUserCompanyGroups();

      // if no loginId is set => use the first one available and set it
      if (sessionStorage.getItem('loginId') === null) {
        sessionStorage.setItem('loginId', this.userCompanyGroups[0].companyLoginId);
      }

      if (sessionStorage.getItem('loginId') !== null) {
        const index = this.userCompanyGroups.findIndex(login => login.companyLoginId === sessionStorage.getItem('loginId'));
        this.selected = index.toString();

        // after getting the userCompanyGroups fetch the platformProfile for the selected loginId
        this.loginIdService.getPlatformProfile(this.loginIdService.createHeaders(this.userCompanyGroups[index].companyLoginId)).then(
            () => {
              this.loginIdService.updateLoginId(this.userCompanyGroups[index]);
              this.displayCompanyLoginSelection();
            })
            .catch(error => {
              this.handleUnexpectedError(error);
            })
      }
    }
  }

  /**
   * Handles unexpected errors by logging the error, showing a user notification, and logging out the user after a delay.
   *
   * @param {any} error - The unexpected error that occurred.
   */
  private handleUnexpectedError(error: any): void {
    Sentry.captureException(error);
    this.snackBarService.showSnackbar(`Es ist ein unerwarteter Fehler aufgetreten. Sie werden jetzt automatisch ausgeloggt. Sollte der Fehler weiterhin bestehen, melden Sie sich gerne bei uns.`, false, 8000);
    setTimeout(() => {
      this.loginIdService.loggedOut(true);
    }, 8000)
  }

  sortUserCompanyGroups(): void {
    const currentUser = JSON.parse(sessionStorage.getItem('user'));
    const ownLoginIndex = this.userCompanyGroups.findIndex(login => login.companyLoginId === currentUser.id);
    if (ownLoginIndex !== -1) {
      const ownCompanyLogin = this.userCompanyGroups[ownLoginIndex];
      this.userCompanyGroups.splice(ownLoginIndex, 1);
      this.userCompanyGroups.unshift(ownCompanyLogin);
    }
  }

  /**
   * Displays the company login selection by setting this.display to true if there is more than one user company group
   */
  private displayCompanyLoginSelection(): void {
    if (this.userCompanyGroups.length > 1) {
      this.display = true;
    }
  }

  ngOnDestroy(): void {
    // do not remove any sessionStorage related data because of second instance of this component within the new_order pages
    this.loginStatusSubscription.unsubscribe();
  }
}
